import React, { Fragment, useEffect, useState, useRef } from 'react-x';
import propTypes from 'prop-types-x';
import classNames from 'classnames';
import _filter from 'lodash/filter.js';
import _findIndex from 'lodash/findIndex.js';
import _sortBy from 'lodash/sortBy.js';
import { GalleryGrid } from './GalleryGrid.jsx';
import { AnimatedTextHeader } from './AnimatedTextHeader.jsx';
import { Divider } from './Divider.jsx';
import { Button } from './Button.jsx';
import { CssAnimation } from './CssAnimation.jsx';
import { VideoPlayer } from './VideoPlayer.jsx';
import { SocialMediaShare } from './SocialMediaShare.jsx';
import { useUpdateEffect } from '../hooks/useUpdateEffect.js';
import { useDragScroll } from '../hooks/useDragScroll.js';
import { useBreakpointChange } from '../hooks/useBreakpointChange.js';
import { multiplier } from '../../utilities/multiplier.js';
import { getSocialMediaShareUrl } from '../../utilities/getSocialMediaShareUrl.js';
import { CORE_GTM_TRACKING_VALUES } from '../../constants/analytics.js';
import { fireGTMTrackingEvent } from '../../utilities/fireGTMTrackingEvent.js';

export function Gallery(props) {

  const {
    header,
    filters,
    images,
    closeLargeImageViewerLabel,
    viewChangeCallback,
    largeImageViewerCaption,
    gridImageOverlay,
    footer,
    loadImagesBlockSize,
    scrollableContainerSelector,
    activeImageUrl,
    socialMediaShareLabel,
    socialMediaShareButtons,
    removeImageId,
    gtmComponentGroup,
    gtmComponentName
  } = props;

  const [imageBlocksLoaded, setImageBlocksLoaded] = useState(1);
  const [activeFilter, setActiveFilter] = useState();
  const [showFilters, setShowFilters] = useState(true);
  const [smallView, setSmallView] = useState(!useBreakpointChange((breakpoint, largerThanPivotBp) => {
    setSmallView(!largerThanPivotBp);
  }, 'md')[1]);
  const currentImageRef = useRef();
  const scrollPositionRef = useRef();
  const scrollableContainerRef = useRef();
  const closeLargeImageViewerRef = useRef();
  const isMountedRef = useRef();
  const hasActiveImage = activeImageUrl && _findIndex(images, ({ base }) => base === activeImageUrl) > -1;
  const imagesOrdered = hasActiveImage ? _sortBy(images, ({ base }) => base === activeImageUrl ? 0 : 1) : images;
  const imagesFiltered = activeFilter ? _filter(imagesOrdered, { data: { atmosphere: activeFilter.toLowerCase() } }) : imagesOrdered;
  const imagesBlock = imagesFiltered.slice(0, loadImagesBlockSize * imageBlocksLoaded);
  const allImagesLoaded = imagesBlock.length >= imagesFiltered.length;
  const [largeImageViewer, setLargeImageViewer] = useState(hasActiveImage ? imagesOrdered[0] : null);
  const block = 'c-gallery';

  function openLargeImageViewer(image) {
    currentImageRef.current = image;
    scrollPositionRef.current = scrollableContainerRef.current.scrollTop;
    const data = JSON.parse(image.dataset.image);
    setLargeImageViewer(data);
  };

  function closeLargeImageViewer() {
    setLargeImageViewer(false);
  };

  const dragEvents = useDragScroll({ speed: 2 });

  function loadNextImageBlock() {
    setImageBlocksLoaded(prevValue => prevValue + 1);
  };

  useEffect(() => {
    scrollableContainerRef.current = scrollableContainerSelector ? document.querySelector(scrollableContainerSelector) : window;
    isMountedRef.current = true;
  }, []);

  useEffect(() => {
    if (largeImageViewer) {
      scrollableContainerRef.current.scrollTo(0, 0);
      closeLargeImageViewerRef.current.focus();

      if (typeof viewChangeCallback === 'function') {
        viewChangeCallback(true);
      }
    }
    else {
      scrollableContainerRef.current.scrollTo({
        top: parseInt(scrollPositionRef.current),
        behavior: 'smooth'
      });

      if (currentImageRef.current) {
        currentImageRef.current.parentElement.focus();
      }

      if (typeof viewChangeCallback === 'function') {
        viewChangeCallback(false);
      }
    }
  }, [largeImageViewer]);

  useEffect(() => {
    if (largeImageViewer) {
      fireGTMTrackingEvent({
        name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
        componentName: gtmComponentName,
        componentGroup: gtmComponentGroup,
        action: CORE_GTM_TRACKING_VALUES.action.viewPicture,
        label: largeImageViewer.large
      });
    }
  }, [largeImageViewer]);

  useUpdateEffect(() => {
    if (activeFilter) {
      fireGTMTrackingEvent({
        name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
        componentName: gtmComponentName,
        componentGroup: gtmComponentGroup,
        action: CORE_GTM_TRACKING_VALUES.action.addFilter,
        label: activeFilter.toLowerCase()
      });
    }
  }, [activeFilter]);

  useUpdateEffect(() => {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
      componentName: gtmComponentName,
      componentGroup: gtmComponentGroup,
      action: CORE_GTM_TRACKING_VALUES.action.clickButton,
      label: showFilters ?
        CORE_GTM_TRACKING_VALUES.label.showFilters :
        CORE_GTM_TRACKING_VALUES.label.hideFilters
    });
  }, [showFilters])

  const filterLabels = (label, Tag = 'div') => (
    <CssAnimation
      enter={{ name: 'short-slide-up-fade-in', duration: 0.5, delay: '0.25, hidden' }}
      triggerEnter={true}
    >
      <Tag className={`${block}__filters-label h6`}>{label}</Tag>
    </CssAnimation>
  );

  const imageExpandIcon = <span className={`${block}__image-expand-icon`}></span>;

  const gridImageOverlayWrapper = image => (
    <CssAnimation
    enter={{ name: 'fade-in', duration: 0.5, delay: `0.75 hidden` }}
    triggerEnter={true}
    > 
      <div>
        {!image.href && imageExpandIcon}
        {image.type === 'video' && <span className={`${block}__image-video-play-btn`}></span>}
        {gridImageOverlay && gridImageOverlay(image)}
      </div>
    </CssAnimation>
  );

  return (
    <div className={classNames({
      [block]: true,
      [`${block}--grid-mode`]: !largeImageViewer,
      [`${block}--view-mode`]: largeImageViewer
    })}>
      {
        largeImageViewer ?
          <div className={`${block}__large-image-viewer`}>
            <Button
            type='tertiary'
            slim={true}
            label={closeLargeImageViewerLabel}
            onClickCallback={closeLargeImageViewer}
            ref={closeLargeImageViewerRef}
            />
            <div className={`${block}__large-image-container col-12 col-lg-10 offset-lg-1`}>
              <div className={`${block}__large-image-background`}>
                <CssAnimation
                enter={{ name: 'vertical-top-bottom-clip', duration: 0.5, easing: 'ease-out' }}
                triggerEnter={true}
                >
                  {
                    largeImageViewer.type === 'video' ?
                      <div>
                        <VideoPlayer
                        src={largeImageViewer.large}
                        gtmComponentName={gtmComponentName}
                        gtmComponentGroup={gtmComponentGroup}
                        />
                      </div>
                      :
                      <div>
                        <div 
                        className={`${block}__blurred-image-background`}
                        style={{ backgroundImage: `url(${largeImageViewer.base})`}}
                        ></div>
                        <img 
                        className={classNames({
                          'c-media__image c-media__image--fit': true,
                          [`${block}__large-image--use-natural-aspect-ratio`]: !largeImageViewer.large
                        })}
                        src={largeImageViewer.large || largeImageViewer.base} 
                        alt={largeImageViewer.alt || 'large gallery image'} 
                        />
                      </div>
                 }
                </CssAnimation>
              </div>
              <CssAnimation
              enter={{ name: 'slide-up-fade-in', duration: 0.5, delay: '0.25, hidden', easing: 'ease-out' }}
              triggerEnter={'in-view-init'}
              renderView={!!largeImageViewerCaption || !!socialMediaShareButtons}
              >
                <div className={`${block}__large-image-caption-container`}>
                  {
                    largeImageViewerCaption &&
                      largeImageViewerCaption(largeImageViewer)
                  }
                  {
                    socialMediaShareButtons &&
                      <SocialMediaShare 
                      label={socialMediaShareLabel}
                      buttons={socialMediaShareButtons}
                      url={getSocialMediaShareUrl(largeImageViewer.id)}
                      media={largeImageViewer.large || largeImageViewer.base}
                      align='center'
                      cssAnimationDelay={0.25}
                      gtmComponentGroup={gtmComponentGroup}
                      gtmComponentName={gtmComponentName}
                      />
                  }
                </div>
              </CssAnimation>
            </div>
          </div>
          :
          <div className={`${block}__header`}>
            {
              header &&
                <Fragment>
                  <AnimatedTextHeader
                  heading={header.heading}
                  copy={header.copy}
                  headingClasses='h3'
                  copyClasses='body col-md-10 offset-md-1 col-lg-8 offset-lg-2 col-xxl-6 offset-xxl-3'
                  />
                  <Divider />
                </Fragment>
            }
            {
              filters &&
                <Fragment>
                  <CssAnimation
                  exit={{ name: 'fade-out', duration: 0.5, easing: 'ease-out' }}
                  triggerExit={!showFilters}
                  removeViewOnExit={true}
                  renderView={showFilters}
                  >
                    <div className={`${block}__filters`}>
                      {
                        smallView &&
                          filterLabels(filters.label)
                      }
                      <div className={`${block}__filter-tabs`} {...dragEvents}>
                        {
                          !smallView &&
                            filterLabels(filters.label, 'span')
                        }
                        {
                          filters.values?.map((value, index) => (
                            <CssAnimation
                            enter={{ name: 'fade-in', duration: 0.5, delay: `${multiplier(index, 0.10, 0.5)}, hidden` }}
                            triggerEnter={true}
                            key={value}
                            >
                              <button
                              className={classNames({
                                [`${block}__filter-tab body`]: true,
                                [`${block}__filter-tab--active`]: value === activeFilter
                              })}
                              onClick={() => setActiveFilter(prevValue => value === prevValue ? undefined : value)}
                              slim={true}
                              >
                                {value}
                              </button>
                            </CssAnimation>
                          ))
                        }
                      </div>
                      <Divider />
                    </div>
                  </CssAnimation>
                  <div className={classNames({
                    [`${block}__filters-toggle-visibilty`]: true,
                    [`${block}__filters-toggle-visibilty--open`]: showFilters
                  })}
                  >
                    <Button
                    type='tertiary'
                    label={showFilters ? filters.buttonLabelHideFilters || 'HIDE FITLERS' : filters.buttonLabelShowFilters || 'SHOW FILTERS'}
                    onClickCallback={() => setShowFilters(prevValue => !prevValue)}
                    cssAnimation={!isMountedRef.current}
                    cssAnimationDelay={0.75}
                    disableTracking={true}
                    slim={true}
                    />
                  </div>
                </Fragment>
            }
          </div>
      }

      <GalleryGrid
      images={imagesBlock}
      onClickCallback={(e) => { removeImageId(), openLargeImageViewer(e) }}
      imageOverlay={gridImageOverlayWrapper}
      />

      <div className={`${block}__footer`}>
        <Divider classes='utils__section-margin-small--uniform' />
        {
          !allImagesLoaded &&
            <div className={`${block}__footer-content`}>
              <div className={`${block}__footer-heading h4`}>{footer.heading}</div>
              <Button
              type='secondary'
              label={footer.buttonLabel || 'LOAD MORE'}
              onClickCallback={loadNextImageBlock}
              gtmName={CORE_GTM_TRACKING_VALUES.name.componentEvent}
              gtmComponentName={gtmComponentName}
              gtmComponentGroup={gtmComponentGroup}
              gtmLabel={CORE_GTM_TRACKING_VALUES.label.loadMore}
              gtmAction={CORE_GTM_TRACKING_VALUES.action.clickButton}
              />
            </div>
        }
      </div>
    </div>
  )
};

Gallery.propTypes = {
  header: propTypes.object,
  filters: propTypes.object,
  footer: propTypes.object,
  images: propTypes.array,
  closeLargeImageViewerLabel: propTypes.string,
  viewChangeCallback: propTypes.func,
  largeImageViewerCaption: propTypes.func,
  gridImageOverlay: propTypes.func,
  loadImagesBlockSize: propTypes.number,
  scrollableContainerSelector: propTypes.string,
  activeImageUrl: propTypes.string,
  socialMediaShareButtons: propTypes.object,
  removeImageId: propTypes.func,
  gtmComponentGroup: propTypes.string,
  gtmComponentName: propTypes.string
};

Gallery.defaultProps = {
  closeLargeImageViewerLabel: 'BACK',
  loadImagesBlockSize: 15,
  removeImageId: () => { },
  gtmComponentName: CORE_GTM_TRACKING_VALUES.componentName.gallery,
  gtmComponentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.campaigns
};